/**
 * CallLogView — THE reducer (CALL_LOG_SPEC.md §6).
 *
 * "Client SDKs maintain ONE reducer (log → {phase, messages, toolCalls,
 * turns, metrics}) fed by any pipe, deduped by seq."
 *
 * This is the whole point of the module: WS attach, WebRTC DataChannel, GET
 * polling and replay are four pipes carrying one envelope, and they must
 * land on one piece of state-building code. A second reducer would be a
 * second vocabulary in disguise.
 *
 * ── Semantics ────────────────────────────────────────────────────────────
 * Ported from the proven `VoiceSession.handleDataChannelMessage` switch
 * (@pinecall/web, src/core/VoiceSession.ts:303-502) — word reassembly,
 * `mergeUserTurn` interim merging, tool-argument parsing — with four
 * deliberate corrections:
 *
 *   1. NO transport coupling and no `trackedTools` filter. The view exposes
 *      every tool call; a UI that wants a subset filters at render time.
 *      Filtering during reduction makes the state depend on widget config.
 *   2. NO `disconnect()` back-edge. VoiceSession called `this.disconnect()`
 *      from inside the switch (:418) — a reducer reaching into a socket.
 *      Here, terminal facts produce an *intent* on the state; the owner of
 *      the transport decides what to do about it.
 *   3. Duration is derived from entry `ts`, never from wall clock, so a
 *      replay of a finished call reproduces the same state as watching it
 *      live (§10.5).
 *   4. `phase` gains "ended", and `bot.corrected` REPLACES the text of the
 *      entry it supersedes (§2) rather than appending a second bubble.
 *
 * ── Idempotence and order independence ───────────────────────────────────
 * `apply()` is idempotent by `seq` and independent of arrival order. It is
 * not a naive running fold: entries are retained in a seq-keyed map and the
 * state is the fold over them in seq order. Applying an entry newer than
 * everything seen (the live case) folds incrementally in O(1); an
 * out-of-order or backfilled entry rebuilds, which is what correctness
 * costs and what makes in-order === shuffled === resumed.
 *
 * ── Immutability ─────────────────────────────────────────────────────────
 * State is produced by structural sharing, never by mutation: an apply
 * yields a new state object, new arrays, and new objects for exactly the
 * entries it changed. Reference equality on a message therefore MEANS "this
 * line did not change" — the contract a memoized transcript line depends on.
 */

import type {
    AnyLogEntry,
    CallSummaryData,
    KnownLogEntry,
    TurnLatency,
    TurnRole,
    WordTiming,
} from "./types.js";
import { isKnownLogEntry, isLogEntry } from "./types.js";

// ── State ────────────────────────────────────────────────────────────────

export type CallPhase =
    | "idle"
    | "ringing"
    | "listening"
    | "thinking"
    | "speaking"
    | "ended";

export type MessageRole = "user" | "bot" | "system";

/** One transcript bubble. `seq` is the entry that created it — `bot.corrected.supersedes` points here. */
export interface CallMessage {
    /** The seq of the entry that created this message. Stable identity. */
    seq: number;
    role: MessageRole;
    text: string;
    /** Provider message id (`user.message.id`, `bot.speaking.id`, …). */
    id?: string;
    /** True while a non-final `user.message` is the latest word on this turn. */
    interim?: boolean;
    /** True between `bot.speaking` and `bot.finished`/`bot.interrupted`. */
    speaking?: boolean;
    interrupted?: boolean;
    /** Set on the system bubble that mirrors a `tool.call`. */
    toolCallId?: string;
    /** Word alignment when TTS provided it (`bot.speaking.words`). */
    words?: WordTiming[];
    /** True once a `bot.corrected` entry replaced this text. */
    corrected?: boolean;
}

export interface CallToolCall {
    id: string;
    name: string;
    args: Record<string, unknown>;
    /** seq of the `tool.call` entry. */
    seq: number;
    /** Present once the correlated `tool.result` arrives. */
    result?: unknown;
    ms?: number;
    error?: string;
    done: boolean;
}

export interface CallTurn {
    turn: number;
    role?: TurnRole;
    latency?: TurnLatency;
    startedAt?: number;
    endedAt?: number;
}

export interface CallMetrics {
    /** Rolled-up distributions, present once `call.summary` lands. */
    summary?: CallSummaryData["metrics"];
    cost?: number;
    recordingUrl?: string;
    /** Mean end-to-end latency over the `turn.end` entries seen so far. */
    e2eMean?: number;
    turnCount: number;
}

/**
 * Something the log says should happen to the transport, surfaced instead of
 * done. Correction #2 above: the reducer never touches a socket.
 */
export interface CallIntent {
    kind: "disconnect";
    reason: string;
    seq: number;
}

/**
 * One row of `state.custom`: the latest value per `(name, id)`, in first-seen
 * order. The wire stays append-only — every `call.log()` is its own entry
 * with its own seq — the upsert is a projection of this reducer only.
 */
export interface CallCustomEntry<V = unknown> {
    name: string;
    /** `data.id ?? String(seq)` — the upsert key together with `name`. */
    id: string;
    value: V;
    /** seq of the entry that LAST set this value. */
    seq: number;
    ts: number;
    /** Server-stamped turn id, when the session has turns. */
    turn?: number;
}

export interface CallLogState {
    phase: CallPhase;
    messages: CallMessage[];
    toolCalls: CallToolCall[];
    turns: CallTurn[];
    metrics: CallMetrics;
    /** False once `call.ended` is applied. */
    live: boolean;
    /** Highest seq applied. The cursor to resume from (`after=`). */
    lastSeq: number;
    /** Call id, from the first entry that carried one. */
    call: string | null;
    agent: string | null;
    /** Seconds, derived from entry `ts` — never from wall clock. */
    duration: number;
    /** True after `log.caught_up`; never inferred from contiguity (§1). */
    caughtUp: boolean;
    /** Declared gaps (§3). Never silently papered over. */
    gaps: { from: number; resumeFrom: number }[];
    userSpeaking: boolean;
    botSpeaking: boolean;
    /** Reason from `call.ended`, if any. */
    endedReason?: string;
    /** Human takeover state (`handoff.*`). */
    handoff: "none" | "requested" | "active";
    /** Skills currently loaded (`skill.loaded` / `skill.unloaded`). */
    skills: string[];
    /** Latest RAG citations (`docs.sources`). */
    sources: unknown[];
    /** Things the log asked the transport to do (§ correction #2). */
    intents: CallIntent[];
    /** Durable `custom` entries, upserted by (name, id). Ephemeral ones never land here. */
    custom: CallCustomEntry[];
}

function emptyState(): CallLogState {
    return {
        phase: "idle",
        messages: [],
        toolCalls: [],
        turns: [],
        metrics: { turnCount: 0 },
        live: true,
        lastSeq: 0,
        call: null,
        agent: null,
        duration: 0,
        caughtUp: false,
        gaps: [],
        userSpeaking: false,
        botSpeaking: false,
        handoff: "none",
        skills: [],
        sources: [],
        intents: [],
        custom: [],
    };
}

/**
 * What a `log.gap` carries in `data.snapshot` — the consolidated state of
 * everything the server could still see when it declared the gap, so a
 * client lands with a populated view instead of an empty one (§3, ag-ui
 * "For Pinecall" 5 and 9). This is the wire contract between
 * `calls_api._snapshot()` and `CallLogView`: the server emits exactly these
 * keys, the reducer hydrates exactly these fields.
 *
 * Rows reuse the reducer's own row types, so hydration is a keyed merge
 * rather than a second fold: `messages` by `seq` (bot bubbles by `id`),
 * `tool_calls` by `id`, `turns` by `turn`, `custom` by `(name, id)`.
 * Scalars are values — the snapshot's word wins. Every key is optional: a
 * missing key leaves the local state untouched, and an unknown key is
 * ignored (§1 forward compatibility).
 *
 * Not carried, by design: `metrics.summary`/`cost` (only `call.summary` sets
 * them, and it is never skipped — a sealed cursor answers 204), `intents`
 * (transport asks, not call facts) and the `log.*` control state.
 */
export interface LogGapSnapshot {
    phase?: CallPhase;
    live?: boolean;
    /** ts of `call.started` — the duration anchor. */
    started_at?: number | null;
    ended_reason?: string;
    user_speaking?: boolean;
    bot_speaking?: boolean;
    handoff?: CallLogState["handoff"];
    skills?: string[];
    sources?: unknown[];
    messages?: CallMessage[];
    tool_calls?: CallToolCall[];
    turns?: CallTurn[];
    custom?: CallCustomEntry[];
}

/**
 * Project a state into the snapshot a `log.gap` would carry for it — the
 * inverse of hydration. The golden test feeds `snapshotOf(prefix)` into a
 * gap and asserts the result equals the full replay; a server that wants
 * to mint gaps against a TS reducer can use it directly.
 */
export function snapshotOf(state: Readonly<CallLogState>, startedAt: number | null = null): LogGapSnapshot {
    return {
        phase: state.phase,
        live: state.live,
        started_at: startedAt,
        ...(state.endedReason !== undefined ? { ended_reason: state.endedReason } : {}),
        user_speaking: state.userSpeaking,
        bot_speaking: state.botSpeaking,
        handoff: state.handoff,
        skills: [...state.skills],
        sources: [...state.sources],
        messages: state.messages.map((m) => ({ ...m })),
        tool_calls: state.toolCalls.map((t) => ({ ...t })),
        turns: state.turns.map((t) => ({ ...t })),
        custom: state.custom.map((c) => ({ ...c })),
    };
}

// ── Helpers ──────────────────────────────────────────────────────────────

/**
 * Merge a user transcript into the CURRENT turn's user bubble — the last
 * user message with no bot reply after it — instead of appending a new one.
 * STT emits several interim AND several final transcripts per turn (Deepgram
 * Flux fires multiple finals), so appending per event duplicates the bubble.
 * A new turn starts only after a bot reply. Ported verbatim in spirit from
 * VoiceSession.ts:38-50.
 */
function mergeUserTurn(
    messages: CallMessage[],
    seq: number,
    id: string,
    text: string,
    interim: boolean,
): void {
    let lastUser = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i]!.role === "user") { lastUser = i; break; }
    }
    let botAfter = false;
    for (let i = lastUser + 1; i < messages.length; i++) {
        if (messages[i]!.role === "bot") { botAfter = true; break; }
    }
    if (lastUser >= 0 && !botAfter) {
        const m = cow(messages, lastUser);
        m.text = text;
        m.interim = interim;
        m.id = id;
        return;
    }
    messages.push({ seq, role: "user", text, id, interim });
}

/**
 * Copy-on-write: replace `list[i]` with a shallow copy and return it, so the
 * caller mutates the copy and never an object a consumer already holds.
 *
 * Structural sharing of the touched path, not a deep clone — untouched
 * siblings keep their references. This matters because the hottest render
 * path in the whole system is word-by-word typing (`bot.word`), where the
 * obvious consumer optimization is a memoized transcript line keyed on the
 * message object. Mutating in place under a stable reference makes that line
 * freeze mid-sentence while the text underneath keeps changing; a fresh
 * ARRAY reference does not help, because the memo never looks at the array.
 * VoiceSession got this right by replacing the tail object per word, so the
 * reducer that supersedes it must not regress there.
 */
function cow<T extends object>(list: T[], i: number): T {
    const copy = { ...list[i]! };
    list[i] = copy;
    return copy;
}

/** Tool args arrive parsed or as a JSON string, depending on the provider. */
function parseArgs(args: Record<string, unknown> | string): Record<string, unknown> {
    if (typeof args !== "string") return args ?? {};
    try {
        const parsed: unknown = JSON.parse(args);
        return typeof parsed === "object" && parsed !== null
            ? (parsed as Record<string, unknown>)
            : {};
    } catch {
        return {};
    }
}

/** A `tool.result` may carry a JSON string; surface the parsed value. */
function parseResult(result: unknown): unknown {
    if (typeof result !== "string") return result;
    try {
        return JSON.parse(result);
    } catch {
        return result;
    }
}

const PHASES: ReadonlySet<string> = new Set(["idle", "ringing", "listening", "thinking", "speaking", "ended"]);
const HANDOFFS: ReadonlySet<string> = new Set(["none", "requested", "active"]);

function isRecord(v: unknown): v is Record<string, unknown> {
    return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** Rows of a snapshot array that are at least objects; anything else is ignored (§1). */
function rows<T>(v: unknown, ok: (r: Record<string, unknown>) => boolean): T[] {
    if (!Array.isArray(v)) return [];
    return v.filter((r): r is Record<string, unknown> => isRecord(r) && ok(r)) as unknown as T[];
}

// ── The reducer ──────────────────────────────────────────────────────────

/** Mutable scratch carried across the fold — never part of the public state. */
interface FoldContext {
    /** Live word buffers keyed by bot message id (`bot.word` reassembly). */
    botWords: Map<string, string[]>;
    /** bot message id → index in `messages`. */
    botIndex: Map<string, number>;
    /** tool call id → index in `toolCalls`. */
    toolIndex: Map<string, number>;
    /** turn number → index in `turns`. */
    turnIndex: Map<number, number>;
    /** message seq → index in `messages` (for `bot.corrected.supersedes`). */
    bySeq: Map<number, number>;
    /** `name + "/" + id` → index in `custom` (the (name, id) upsert). */
    customIndex: Map<string, number>;
    /** ts of the first entry / of `call.started`. */
    startTs: number | null;
    lastTs: number | null;
    e2eSum: number;
    e2eN: number;
}

function emptyContext(): FoldContext {
    return {
        botWords: new Map(),
        botIndex: new Map(),
        toolIndex: new Map(),
        turnIndex: new Map(),
        bySeq: new Map(),
        customIndex: new Map(),
        startTs: null,
        lastTs: null,
        e2eSum: 0,
        e2eN: 0,
    };
}

/**
 * A single log view. Feed it entries from any pipe, in any order, as many
 * times as you like; read `state`.
 */
export class CallLogView {
    #entries = new Map<number, KnownLogEntry>();
    #state: CallLogState = emptyState();
    #ctx: FoldContext = emptyContext();
    #maxSeq = -1;
    /**
     * Every `log.gap` applied, in arrival order. Markers are never stored in
     * `#entries` (§5), but a gap HYDRATES — so an out-of-order rebuild must
     * re-step each gap at its place in the seq order or the hydrated stretch
     * would evaporate.
     */
    #gaps: KnownLogEntry[] = [];
    #listeners = new Set<(state: CallLogState) => void>();

    /**
     * How many entries to retain for out-of-order rebuilds. The retained
     * window bounds memory on a long call; entries older than the window are
     * still reflected in the state, they just cannot be re-folded. Default
     * 10_000 (the hot buffer is 1000, so this is generous by 10x).
     */
    constructor(private readonly retain = 10_000) {}

    /**
     * Read-only snapshot, safe to retain and to compare by reference.
     *
     * Every state-affecting apply produces a new state object, new
     * `messages`/`toolCalls`/`turns`/`custom` arrays, and new objects for exactly the
     * entries that changed — untouched siblings keep their identity. So
     * `prev.messages[3] === next.messages[3]` is a truthful "this line did
     * not change", which is what makes a memoized transcript line correct
     * rather than merely fast.
     */
    get state(): Readonly<CallLogState> {
        return this.#state;
    }

    /** The cursor to resume from: `?after=<lastSeq>`. */
    get lastSeq(): number {
        return this.#state.lastSeq;
    }

    /** Subscribe to state changes. Returns an unsubscribe function. */
    subscribe(fn: (state: CallLogState) => void): () => void {
        this.#listeners.add(fn);
        return () => { this.#listeners.delete(fn); };
    }

    /** The entries this view retains, in seq order — the resume payload. */
    entries(): KnownLogEntry[] {
        return [...this.#entries.keys()].sort((a, b) => a - b).map((s) => this.#entries.get(s)!);
    }

    /** True if this exact seq has already been applied. */
    has(seq: number): boolean {
        return this.#entries.has(seq);
    }

    /**
     * Apply one entry. Idempotent by `seq`, order-independent.
     * Returns true if the view changed.
     */
    apply(entry: AnyLogEntry): boolean {
        if (!isLogEntry(entry)) return false;
        // §1: unknown types MUST be ignored, not rejected.
        if (!isKnownLogEntry(entry)) return false;

        // §5: control markers are FULL envelopes whose seq REPEATS the last
        // seq delivered — the dedupe below would swallow them, and storing
        // them would poison entries(), which is the resume payload. Dispatch
        // immediately; never store, never advance the cursor.
        if (entry.type === "log.caught_up" || entry.type === "log.gap") {
            if (entry.type === "log.gap") this.#gaps.push(entry);
            this.#state = {
                ...this.#state,
                messages: [...this.#state.messages],
                toolCalls: [...this.#state.toolCalls],
                turns: [...this.#state.turns],
                custom: [...this.#state.custom],
            };
            this.#step(this.#state, this.#ctx, entry);
            this.#finish(this.#state, this.#ctx);
            for (const fn of this.#listeners) fn(this.#state);
            return true;
        }

        // §1: consumers MUST dedupe by seq — transports overlap.
        if (this.#entries.has(entry.seq)) return false;

        this.#entries.set(entry.seq, entry);

        if (entry.seq > this.#maxSeq) {
            // Live path: strictly newer than anything seen — fold forward.
            this.#maxSeq = entry.seq;
            // New top-level and array references so snapshot consumers
            // (useSyncExternalStore and friends) see a change.
            this.#state = {
                ...this.#state,
                messages: [...this.#state.messages],
                toolCalls: [...this.#state.toolCalls],
                turns: [...this.#state.turns],
                custom: [...this.#state.custom],
            };
            this.#step(this.#state, this.#ctx, entry);
            this.#finish(this.#state, this.#ctx);
            this.#trim();
        } else {
            // Backfill / out-of-order: the fold must run in seq order.
            this.#trim();
            this.#rebuild();
        }

        for (const fn of this.#listeners) fn(this.#state);
        return true;
    }

    /** Apply many. Returns how many changed the view. */
    applyAll(entries: readonly AnyLogEntry[]): number {
        let n = 0;
        for (const e of entries) if (this.apply(e)) n++;
        return n;
    }

    /** Drop everything and start over. */
    reset(): void {
        this.#entries.clear();
        this.#state = emptyState();
        this.#ctx = emptyContext();
        this.#maxSeq = -1;
        this.#gaps = [];
        for (const fn of this.#listeners) fn(this.#state);
    }

    // ── Internals ──

    #trim(): void {
        if (this.#entries.size <= this.retain) return;
        const keys = [...this.#entries.keys()].sort((a, b) => a - b);
        const drop = keys.length - this.retain;
        for (let i = 0; i < drop; i++) this.#entries.delete(keys[i]!);
    }

    #rebuild(): void {
        const state = emptyState();
        const ctx = emptyContext();
        // Gaps are re-stepped in seq order, interleaved with the entries: a
        // gap's seq sits right before the first entry it resumes at, so
        // everything at or below it folds first, then the snapshot lands,
        // then the rest — the order the transport delivered.
        const gaps = [...this.#gaps].sort((a, b) => a.seq - b.seq);
        let g = 0;
        for (const entry of this.entries()) {
            while (g < gaps.length && gaps[g]!.seq < entry.seq) this.#step(state, ctx, gaps[g++]!);
            this.#step(state, ctx, entry);
        }
        while (g < gaps.length) this.#step(state, ctx, gaps[g++]!);
        this.#finish(state, ctx);
        // Caught-up is a TRANSPORT signal, not a log fact: the marker is never
        // stored, so a rebuild cannot re-derive it. Carry the transport's
        // last word across.
        state.caughtUp = this.#state.caughtUp;
        this.#state = state;
        this.#ctx = ctx;
    }

    #finish(state: CallLogState, ctx: FoldContext): void {
        // Correction #3: duration comes from entry ts, never wall clock.
        state.duration =
            ctx.startTs !== null && ctx.lastTs !== null
                ? Math.max(0, ctx.lastTs - ctx.startTs)
                : 0;
        state.metrics = {
            ...state.metrics,
            turnCount: state.turns.length,
            ...(ctx.e2eN > 0 ? { e2eMean: ctx.e2eSum / ctx.e2eN } : {}),
        };
    }

    /**
     * Fold one entry into `state`. Exhaustive over the closed vocabulary —
     * the `never` in the default arm is the compile-time guarantee that a
     * new §2 type cannot be added without teaching the reducer about it.
     */
    #step(state: CallLogState, ctx: FoldContext, entry: KnownLogEntry): void {
        state.lastSeq = Math.max(state.lastSeq, entry.seq);
        if (entry.call && !state.call) state.call = entry.call;
        if (entry.agent && !state.agent) state.agent = entry.agent;
        // Control markers are minted by the transport at wall-clock time —
        // they say nothing about when the call happened, so they never move
        // the duration anchors (correction #3).
        if (entry.type !== "log.caught_up" && entry.type !== "log.gap") {
            if (ctx.startTs === null) ctx.startTs = entry.ts;
            ctx.lastTs = ctx.lastTs === null ? entry.ts : Math.max(ctx.lastTs, entry.ts);
        }

        const messages = state.messages;

        switch (entry.type) {
            // ── Lifecycle ──
            case "call.ringing":
                state.phase = "ringing";
                break;

            case "call.started":
                ctx.startTs = entry.ts;
                state.phase = "listening";
                state.live = true;
                break;

            case "call.ended":
                state.phase = "ended";
                state.live = false;
                state.userSpeaking = false;
                state.botSpeaking = false;
                state.endedReason = entry.data.reason;
                // Correction #2: surface the intent, never call disconnect().
                state.intents = [
                    ...state.intents,
                    { kind: "disconnect", reason: entry.data.reason, seq: entry.seq },
                ];
                break;

            case "call.summary":
                state.metrics = {
                    ...state.metrics,
                    summary: entry.data.metrics,
                    ...(entry.data.cost !== undefined ? { cost: entry.data.cost } : {}),
                    ...(entry.data.recording_url
                        ? { recordingUrl: entry.data.recording_url }
                        : {}),
                };
                state.live = false;
                if (state.phase !== "ended") state.phase = "ended";
                break;

            // ── Speech & transcript ──
            case "user.speaking":
                state.userSpeaking = entry.data.active;
                if (entry.data.active && state.phase !== "ended") state.phase = "listening";
                break;

            case "user.message": {
                if (entry.data.text) {
                    mergeUserTurn(
                        messages,
                        entry.seq,
                        entry.data.id,
                        entry.data.text,
                        !entry.data.final,
                    );
                    this.#reindex(ctx, messages);
                }
                if (entry.data.final) {
                    state.userSpeaking = false;
                    if (state.phase !== "ended") state.phase = "thinking";
                }
                break;
            }

            case "bot.speaking": {
                const id = entry.data.id;
                ctx.botWords.set(id, []);
                const idx = ctx.botIndex.get(id);
                const words = entry.data.words;
                if (idx === undefined) {
                    messages.push({
                        seq: entry.seq,
                        role: "bot",
                        text: entry.data.text ?? "",
                        id,
                        speaking: true,
                        ...(words ? { words } : {}),
                    });
                    this.#reindex(ctx, messages);
                } else {
                    const m = cow(messages, idx);
                    if (entry.data.text) m.text = entry.data.text;
                    if (words) m.words = words;
                    m.speaking = true;
                }
                state.botSpeaking = true;
                if (state.phase !== "ended") state.phase = "speaking";
                break;
            }

            case "bot.word": {
                // Ephemeral typing effect. §2's bot.word carries no index, so
                // reassembly is append-in-seq-order — deterministic because the
                // fold always runs in seq order.
                const id = entry.data.id;
                let buf = ctx.botWords.get(id);
                if (!buf) { buf = []; ctx.botWords.set(id, buf); }
                buf.push(entry.data.w);
                const text = buf.join(" ");
                const idx = ctx.botIndex.get(id);
                if (idx === undefined) {
                    messages.push({ seq: entry.seq, role: "bot", text, id, speaking: true });
                    this.#reindex(ctx, messages);
                } else {
                    // Never clobber an authoritative bot.speaking.text with a
                    // partial word buffer.
                    const m = cow(messages, idx);
                    if (!m.corrected && buf.length > 0 && m.text.length <= text.length) {
                        m.text = text;
                    }
                    m.speaking = true;
                }
                state.botSpeaking = true;
                if (state.phase !== "ended") state.phase = "speaking";
                break;
            }

            case "bot.finished": {
                const idx = ctx.botIndex.get(entry.data.id);
                if (idx !== undefined) {
                    // The LLM went straight to a tool call: drop the phantom
                    // bubble rather than copying it only to remove it.
                    if (!messages[idx]!.text) {
                        messages.splice(idx, 1);
                        this.#reindex(ctx, messages);
                    } else {
                        cow(messages, idx).speaking = false;
                    }
                }
                state.botSpeaking = false;
                if (state.phase !== "ended") state.phase = "listening";
                break;
            }

            case "bot.interrupted": {
                const idx = ctx.botIndex.get(entry.data.id);
                if (idx !== undefined) {
                    const m = cow(messages, idx);
                    m.speaking = false;
                    m.interrupted = true;
                }
                state.botSpeaking = false;
                if (state.phase !== "ended") state.phase = "listening";
                break;
            }

            case "bot.corrected": {
                // Correction #4 / §2: replace the text of the superseded entry.
                // An event, not a mutation — replay reaches the same place.
                const idx = ctx.bySeq.get(entry.data.supersedes);
                if (idx !== undefined) {
                    const m = cow(messages, idx);
                    m.text = entry.data.text;
                    m.corrected = true;
                } else {
                    messages.push({
                        seq: entry.seq,
                        role: "bot",
                        text: entry.data.text,
                        id: entry.data.id,
                        corrected: true,
                    });
                    this.#reindex(ctx, messages);
                }
                break;
            }

            // ── Turns & latency ──
            case "turn.start": {
                const i = ctx.turnIndex.get(entry.data.turn);
                if (i === undefined) {
                    ctx.turnIndex.set(entry.data.turn, state.turns.length);
                    state.turns.push({
                        turn: entry.data.turn,
                        role: entry.data.role,
                        startedAt: entry.ts,
                    });
                } else {
                    state.turns[i] = {
                        ...state.turns[i]!,
                        role: entry.data.role,
                        startedAt: entry.ts,
                    };
                }
                break;
            }

            case "turn.end": {
                const i = ctx.turnIndex.get(entry.data.turn);
                // A hydrated turn may already carry an e2e (the gap snapshot
                // folds the entries it is served with): retire it before the
                // entry's own counts, so re-application never double-counts.
                const prev = i !== undefined ? state.turns[i]!.latency?.e2e : undefined;
                if (typeof prev === "number") { ctx.e2eSum -= prev; ctx.e2eN -= 1; }
                if (i === undefined) {
                    ctx.turnIndex.set(entry.data.turn, state.turns.length);
                    state.turns.push({
                        turn: entry.data.turn,
                        latency: entry.data.latency,
                        endedAt: entry.ts,
                    });
                } else {
                    state.turns[i] = {
                        ...state.turns[i]!,
                        latency: entry.data.latency,
                        endedAt: entry.ts,
                    };
                }
                if (typeof entry.data.latency?.e2e === "number") {
                    ctx.e2eSum += entry.data.latency.e2e;
                    ctx.e2eN += 1;
                }
                break;
            }

            // ── Tools, knowledge, skills ──
            case "tool.call": {
                // Correction #1: no trackedTools filter — expose everything.
                const id = entry.data.id;
                if (ctx.toolIndex.get(id) === undefined) {
                    ctx.toolIndex.set(id, state.toolCalls.length);
                    state.toolCalls.push({
                        id,
                        name: entry.data.name,
                        args: parseArgs(entry.data.args),
                        seq: entry.seq,
                        done: false,
                    });
                    messages.push({
                        seq: entry.seq,
                        role: "system",
                        text: `Using ${entry.data.name}…`,
                        toolCallId: id,
                    });
                    this.#reindex(ctx, messages);
                }
                break;
            }

            case "tool.result": {
                const i = ctx.toolIndex.get(entry.data.id);
                if (i !== undefined) {
                    state.toolCalls[i] = {
                        ...state.toolCalls[i]!,
                        result: parseResult(entry.data.result),
                        ms: entry.data.ms,
                        ...(entry.data.error ? { error: entry.data.error } : {}),
                        done: true,
                    };
                } else {
                    // Result without its call (backlog started mid-pair).
                    ctx.toolIndex.set(entry.data.id, state.toolCalls.length);
                    state.toolCalls.push({
                        id: entry.data.id,
                        name: entry.data.name,
                        args: {},
                        seq: entry.seq,
                        result: parseResult(entry.data.result),
                        ms: entry.data.ms,
                        ...(entry.data.error ? { error: entry.data.error } : {}),
                        done: true,
                    });
                }
                for (let i = 0; i < messages.length; i++) {
                    if (messages[i]!.toolCallId === entry.data.id) {
                        cow(messages, i).text = entry.data.error
                            ? `${entry.data.name} failed`
                            : `${entry.data.name}`;
                    }
                }
                break;
            }

            case "docs.sources":
                state.sources = entry.data.sources ?? [];
                break;

            case "skill.loaded":
                if (!state.skills.includes(entry.data.skill)) {
                    state.skills = [...state.skills, entry.data.skill];
                }
                break;

            case "skill.unloaded":
                state.skills = state.skills.filter((s) => s !== entry.data.skill);
                break;

            // ── Metrics & control ──
            case "audio.metrics":
                // Ephemeral; rolled up into call.summary server-side (§4).
                break;

            case "handoff.requested":
                state.handoff = "requested";
                break;
            case "handoff.active":
                state.handoff = "active";
                break;
            case "handoff.released":
                state.handoff = "none";
                break;

            case "supervisor.said":
            case "supervisor.whispered":
                // A gap snapshot may already carry this line (its fold covers
                // the entries it is served with) — keyed by seq, once.
                if (!ctx.bySeq.has(entry.seq)) {
                    messages.push({
                        seq: entry.seq,
                        role: "system",
                        text: entry.data.text,
                    });
                    this.#reindex(ctx, messages);
                }
                break;

            // ── Log control ──
            case "log.caught_up":
                state.caughtUp = true;
                break;

            case "log.gap":
                // §3: declare the gap, land the snapshot, move the cursor.
                // `after=` is exclusive (seq > after), so the cursor that
                // resumes AT resume_from is resume_from - 1 — the seq the
                // server stamps on the marker itself.
                state.gaps = [
                    ...state.gaps,
                    { from: entry.data.from, resumeFrom: entry.data.resume_from },
                ];
                state.caughtUp = false;
                state.lastSeq = Math.max(state.lastSeq, entry.data.resume_from - 1);
                if (isRecord(entry.data.snapshot)) {
                    this.#hydrate(state, ctx, entry.data.snapshot as LogGapSnapshot);
                }
                break;

            // ── Custom entries (`call.log()`) ──
            case "custom": {
                // Ephemeral: fanned out to listeners, never projected — the
                // store never has it, so live state must not have it either
                // (replay === live). Durable: upsert by (name, id) — the row
                // is REPLACED wholesale (value, seq, ts, turn), never merged,
                // so the final state never depends on which entries were
                // skipped. Absent id → the entry's own seq, i.e. append.
                if (entry.ephemeral) break;
                const id = entry.data.id ?? String(entry.seq);
                const key = `${entry.data.name}/${id}`;
                const row: CallCustomEntry = {
                    name: entry.data.name,
                    id,
                    value: entry.data.value,
                    seq: entry.seq,
                    ts: entry.ts,
                    ...(entry.data.turn !== undefined ? { turn: entry.data.turn } : {}),
                };
                const i = ctx.customIndex.get(key);
                if (i === undefined) {
                    ctx.customIndex.set(key, state.custom.length);
                    state.custom.push(row);
                } else {
                    state.custom[i] = row;
                }
                break;
            }

            default: {
                // Exhaustiveness: adding a §2 type without a case fails to compile.
                const _never: never = entry;
                void _never;
            }
        }
    }

    /**
     * Merge a `log.gap` snapshot into `state` (ag-ui "For Pinecall" 9:
     * MESSAGES_SNAPSHOT's rule — merge by id, never wipe).
     *
     *   - `messages`: keyed by `seq` (bot bubbles by `id`). A snapshot row
     *     REPLACES its local counterpart and a new one is inserted; local-only
     *     rows survive. Result is re-sorted by seq — the order the fold would
     *     have produced.
     *   - `tool_calls`: keyed by `id`, same rule, sorted by seq.
     *   - `turns`: keyed by `turn`, field-merged (`{...local, ...row}`) so a
     *     `turn.end` already folded locally is not lost to a snapshot that
     *     only saw the `turn.start`. Latency sums are recomputed from the
     *     merged turns.
     *   - `custom`: keyed by `(name, id)`; the row with the HIGHER seq wins —
     *     the one rule the upsert already has. First-seen order kept, new
     *     keys appended.
     *   - scalars (`phase`, `live`, `started_at`, `ended_reason`,
     *     `user_speaking`, `bot_speaking`, `handoff`, `skills`, `sources`):
     *     values — present wins, absent leaves local alone.
     *
     * Re-applying the entries the snapshot was folded from is a no-op for
     * what it carried: every case in `#step` is keyed the same way.
     */
    #hydrate(state: CallLogState, ctx: FoldContext, snap: LogGapSnapshot): void {
        if (typeof snap.phase === "string" && PHASES.has(snap.phase)) state.phase = snap.phase;
        if (typeof snap.live === "boolean") state.live = snap.live;
        if (typeof snap.started_at === "number") ctx.startTs = snap.started_at;
        if (typeof snap.ended_reason === "string") state.endedReason = snap.ended_reason;
        if (typeof snap.user_speaking === "boolean") state.userSpeaking = snap.user_speaking;
        if (typeof snap.bot_speaking === "boolean") state.botSpeaking = snap.bot_speaking;
        if (typeof snap.handoff === "string" && HANDOFFS.has(snap.handoff)) state.handoff = snap.handoff;
        if (Array.isArray(snap.skills)) state.skills = snap.skills.filter((x) => typeof x === "string");
        if (Array.isArray(snap.sources)) state.sources = [...snap.sources];

        const msgRows = rows<CallMessage>(snap.messages, (r) => typeof r.seq === "number" && typeof r.role === "string");
        if (msgRows.length) {
            const messages = state.messages;
            for (const r of msgRows) {
                const row: CallMessage = { ...r, text: typeof r.text === "string" ? r.text : "" };
                const idx = row.role === "bot" && row.id !== undefined
                    ? ctx.botIndex.get(row.id) ?? ctx.bySeq.get(row.seq)
                    : ctx.bySeq.get(row.seq);
                if (idx === undefined) messages.push(row);
                else messages[idx] = row;
                this.#reindex(ctx, messages);
            }
            messages.sort((a, b) => a.seq - b.seq);
            this.#reindex(ctx, messages);
        }

        const toolRows = rows<CallToolCall>(snap.tool_calls, (r) => typeof r.id === "string" && typeof r.seq === "number");
        if (toolRows.length) {
            for (const r of toolRows) {
                const row: CallToolCall = {
                    ...r,
                    name: typeof r.name === "string" ? r.name : "",
                    args: isRecord(r.args) ? r.args : {},
                    done: r.done === true,
                };
                const i = ctx.toolIndex.get(row.id);
                if (i === undefined) state.toolCalls.push(row);
                else state.toolCalls[i] = row;
                ctx.toolIndex.set(row.id, state.toolCalls.length - 1);
            }
            state.toolCalls.sort((a, b) => a.seq - b.seq);
            ctx.toolIndex.clear();
            state.toolCalls.forEach((t, i) => ctx.toolIndex.set(t.id, i));
        }

        const turnRows = rows<CallTurn>(snap.turns, (r) => typeof r.turn === "number");
        if (turnRows.length) {
            for (const r of turnRows) {
                const i = ctx.turnIndex.get(r.turn);
                if (i === undefined) state.turns.push({ ...r });
                else state.turns[i] = { ...state.turns[i]!, ...r };
                ctx.turnIndex.set(r.turn, i ?? state.turns.length - 1);
            }
            state.turns.sort((a, b) => a.turn - b.turn);
            ctx.turnIndex.clear();
            ctx.e2eSum = 0;
            ctx.e2eN = 0;
            state.turns.forEach((t, i) => {
                ctx.turnIndex.set(t.turn, i);
                if (typeof t.latency?.e2e === "number") { ctx.e2eSum += t.latency.e2e; ctx.e2eN += 1; }
            });
        }

        const customRows = rows<CallCustomEntry>(snap.custom, (r) => typeof r.name === "string" && typeof r.seq === "number");
        for (const r of customRows) {
            const id = r.id !== undefined && r.id !== null ? String(r.id) : String(r.seq);
            const row: CallCustomEntry = { ...r, id };
            const key = `${row.name}/${id}`;
            const i = ctx.customIndex.get(key);
            if (i === undefined) {
                ctx.customIndex.set(key, state.custom.length);
                state.custom.push(row);
            } else if (row.seq >= state.custom[i]!.seq) {
                state.custom[i] = row;
            }
        }
    }

    /** Rebuild the id→index maps after a splice/push. Cheap: messages are small. */
    #reindex(ctx: FoldContext, messages: CallMessage[]): void {
        ctx.botIndex.clear();
        ctx.bySeq.clear();
        for (let i = 0; i < messages.length; i++) {
            const m = messages[i]!;
            ctx.bySeq.set(m.seq, i);
            if (m.role === "bot" && m.id !== undefined) ctx.botIndex.set(m.id, i);
        }
    }
}

/** Build a view from a batch of entries. */
export function createCallLogView(entries?: readonly AnyLogEntry[]): CallLogView {
    const view = new CallLogView();
    if (entries) view.applyAll(entries);
    return view;
}
